import { addDays, format } from 'date-fns'
import { applySnapshot, getSnapshot, types } from 'mobx-state-tree'

import { extendLogger, LoggerContexts } from '../../utils/logger'
import { events } from '../analytics/analytics-events'
import { TaskStatus, WordpressDailyInspirationModel } from '../api-store'
import { withRootStore } from '../extensions'

import type { WordpressDailyInspirationModelType } from '../api-store'
import type { Instance, SnapshotOut } from 'mobx-state-tree'
const logger = extendLogger(LoggerContexts.SessionDaily)
const defaultDateFormat = 'MM/dd/yyyy'
const EMOJIS = ['👑', '👏', '🙌', '🔥', '💃🏻']

export const AcfModal = types.model('AcfModal').props({
  day: types.string,
  external_or_discussion_link: types.string,
  motivational_message: types.string,
  week: types.string
})
export const DailyTaskModel = types.model('DailyTaskModel').props({
  daily_task: types.string,
  daily_task_category: types.string,
  daily_task_subheader: types.string,
  daily_task_deeplink_url: types.string,
  daily_task_video_url: types.string,
  daily_task_icon: types.string,
  day: types.string,
  week: types.string,
  id: types.number,
  status: typeof TaskStatus
})
export const DailyInspoReactionModel = types.model('DailyInspoReactionModel').props({
  emoji: types.string,
  count: types.number
})
export const DailyTaskStatusModel = types.model('DailyTaskModel').props({
  taskId: types.number,
  taskName: types.string,
  status: typeof TaskStatus,
  day: types.string,
  week: types.string,
  category: types.string
})
export const LatestResponsesModal = types.model('LatestResponsesModal').props({
  response: types.string,
  userName: types.string,
  avatar: types.string,
  emojis: types.array(DailyInspoReactionModel)
})
export const AllInspirationResponsesModal = types.model('AllInspirationResponsesModal').props({
  _id: types.string,
  response: types.string,
  userName: types.string,
  userId: types.string,
  avatar: types.string,
  emojis: types.array(DailyInspoReactionModel)
})

export interface DailyInspoReactionType extends Instance<typeof DailyInspoReactionModel> {}
export interface DailyTaskStatusType extends Instance<typeof DailyTaskStatusModel> {}
export interface DailyTaskType extends Instance<typeof DailyTaskModel> {}
export type DailyTaskSnapshot = SnapshotOut<typeof DailyTaskModel>
export interface LatestResponsesModalType extends Instance<typeof LatestResponsesModal> {}
export interface AllInspirationResponsesModalType extends Instance<typeof AllInspirationResponsesModal> {}

/**
 * A SessionUserStore model.
 */
export const SessionDailyStoreModel = types
  .model('SessionDailyStoreModel')
  .props({
    dailyInspoReaction: types.optional(types.array(DailyInspoReactionModel), []),
    dailyTask: types.optional(types.array(DailyTaskStatusModel), []),
    selectedDailyTask: types.maybe(DailyTaskStatusModel),
    dailyInspirationalQuoteData: types.maybe(WordpressDailyInspirationModel),
    dailyTasklist: types.optional(types.array(DailyTaskModel), []),
    taskDay: types.optional(types.number, 0),
    totalResponse: types.optional(types.number, 0),
    recentResponses: types.optional(types.array(LatestResponsesModal), []),
    allInspirationResponses: types.optional(types.array(AllInspirationResponsesModal), []),
    pageNumber: types.optional(types.number, 0),
    hasMore: types.optional(types.boolean, true),
    userResponseCount: types.optional(types.number, 0)
  })
  .extend(withRootStore)
  .views((self) => ({
    get completedTaskListLength() {
      const completedTaskList = self.dailyTasklist.filter((task) => task.status === TaskStatus.complete)
      return completedTaskList.length
    }
  }))
  .views((self) => ({
    get isTodaysFirstTask() {
      return self.completedTaskListLength === 0
    },
    get isTodaysLastTask() {
      return self.completedTaskListLength === self.dailyTasklist.length - 1
    },
    get isTodaysTaskCompleted() {
      return self.completedTaskListLength === self.dailyTasklist.length
    }
  }))
  .actions((self) => ({
    setDailyInspoReactions(value: DailyInspoReactionType[]) {
      self.dailyInspoReaction.replace(value)
    },
    setDailyTask(value: DailyTaskStatusType[]) {
      self.dailyTask.replace(value)
    },
    setSelectedDailyTask(value: DailyTaskStatusType) {
      if (value) {
        self.selectedDailyTask = value
      }
    },
    clearSelectedDailyTask() {
      self.selectedDailyTask = undefined
    },
    setDailyInspirationalQuoteData(value: WordpressDailyInspirationModelType) {
      self.dailyInspirationalQuoteData = value
    },
    setDailyTasklist(value: DailyTaskType[]) {
      logger.info('setDailyTasklist', value)
      self.dailyTasklist.replace(value)
    },
    setTotalResponse(value: number) {
      self.totalResponse = value
    },
    setRecentResponses(value: LatestResponsesModalType[]) {
      self.recentResponses.replace(value)
    },
    setAllInspirationResponses(value: AllInspirationResponsesModalType[]) {
      self.allInspirationResponses.replace(value)
    },
    setPageNumber(value: number) {
      self.pageNumber = value
    },
    setHasMore(value: boolean) {
      self.hasMore = value
    },
    //
    // testing only:
    setUserResponseCount(value: number) {
      self.userResponseCount = value
    },
    clearUserResponseCount() {
      self.userResponseCount = 0
    },
    incrementTaskDay() {
      self.taskDay = self.taskDay + 1
    },
    clearTaskDay() {
      self.taskDay = 0
    }
    // testing only^
    //
  }))
  .actions((self) => ({
    async getDailyInspirationalQuote() {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        if (userId) {
          const todaysDate = addDays(new Date(), self.taskDay)
          const formattedDate = format(todaysDate, defaultDateFormat)
          const response = await self.rootStore.api.queryGetDailyInspirational(
            { date: formattedDate, userId },
            '_id createdAt id acf {day external_or_discussion_link motivational_message week}',
            {
              fetchPolicy: 'no-cache'
            }
          )
          logger.debug('getDailyInspirationalQuote1', response.getDailyInspirational)
          self.setDailyInspirationalQuoteData(response.getDailyInspirational)
        }
      } catch (error) {
        logger.error('getDailyInspirationalQuote error', error)
      }
    },
    async getDailyTask() {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        const todaysDate = addDays(new Date(), self.taskDay)
        const formattedDate = format(todaysDate, defaultDateFormat)
        if (userId) {
          const payload = {
            userId,
            date: formattedDate
          }
          logger.info('getDailyTask payload: ', payload)
          const response = await self.rootStore.api.queryGetDailyTask(payload, { fetchPolicy: 'no-cache' })
          logger.info('getDailyTask response', response.getDailyTask)
          self.setDailyTasklist(response.getDailyTask)
        }
      } catch (error) {
        logger.error('getDailyTask error', error)
      }
    },
    sortEmojiCountArray(emojiReactArray: DailyInspoReactionType[]) {
      // Note:- Sort incoming emoji count as per listed in EMOJIS
      const emojiArrayCount: DailyInspoReactionType[] = []
      EMOJIS.forEach((item) => {
        const data = emojiReactArray.filter((emojiData: DailyInspoReactionType) => emojiData.emoji === item)
        if (data.length > 0) {
          emojiArrayCount.push(data[0])
        } else {
          emojiArrayCount.push({
            emoji: item,
            count: 0
          })
        }
      })
      self.setDailyInspoReactions(emojiArrayCount)
    },
    async reactOnDailyInspirationalQuote(emoji: string) {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        const quoteId = self.dailyInspirationalQuoteData?.id ?? ''
        if (userId && quoteId) {
          const todaysDate = addDays(new Date(), self.taskDay)
          const formattedDate = format(todaysDate, defaultDateFormat)
          const payload = {
            id: quoteId,
            emoji,
            userId,
            date: formattedDate
          }
          const response = await self.rootStore.api.mutateUpdateDailyInspiration(payload)
          logger.debug('reactOnDailyInspirationalQuote', response.updateDailyInspiration)
          const reactionArray = response.updateDailyInspiration.emoji ?? []
          self.rootStore.session.sortEmojiCountArray(reactionArray)
        }
      } catch (error) {
        logger.error('reactOnDailyInspirationalQuote Error: ', error)
        throw error
      }
    },
    async getInspoReactionsUsingId() {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        const id = self.dailyInspirationalQuoteData?.id ?? ''
        if (userId && id) {
          const response = await self.rootStore.api.queryGetInspirationById({ id, userId }, { fetchPolicy: 'no-cache' })
          logger.debug('getInspoReactionsUsingId response', response.getInspirationById)
          self.rootStore.session.sortEmojiCountArray(response.getInspirationById.emoji)
          self.rootStore.session.setTotalResponse(response.getInspirationById.responses.totalResponses)
          self.rootStore.session.setRecentResponses(response.getInspirationById.responses.latestResponses)
        }
      } catch (error) {
        logger.error('getInspoReactionsUsingId Error: ', error)
        throw error
      }
    },
    async updateDailyTaskStatus() {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        logger.info('updateDailyTaskStatus isTaskComplete?', self.selectedDailyTask)
        if (userId && self.selectedDailyTask) {
          const todaysDate = addDays(new Date(), self.taskDay)
          const formattedDate = format(todaysDate, defaultDateFormat)
          const payload = {
            status: TaskStatus.complete,
            userId,
            id: self.selectedDailyTask.taskId,
            day: self.selectedDailyTask.day,
            week: self.selectedDailyTask.week,
            date: formattedDate
          }
          logger.info('updateDailyTaskStatus payload', payload)
          const response = await self.rootStore.api.mutateUpdateDailyTask(payload)
          if (self.isTodaysLastTask) {
            self.rootStore.analytics.setEvent(events.dailyTask.daily_tasks_completed, {
              userId,
              day: self.selectedDailyTask.day,
              week: self.selectedDailyTask.week
            })
          }
          self.rootStore.analytics.setEvent(events.dailyTask.task_completed, {
            userId,
            category: self.selectedDailyTask.category,
            day: self.selectedDailyTask.day,
            week: self.selectedDailyTask.week,
            task_name: self.selectedDailyTask.taskName
          })
          self.rootStore.session.clearSelectedDailyTask()
          logger.info('updateDailyTaskStatus', response.updateDailyTask)
          if (response.updateDailyTask) {
            self.setDailyTasklist(response.updateDailyTask)
          }
        }
      } catch (error) {
        logger.error('updateDailyTaskStatus Error: ', error)
        throw error
      }
    },
    async getWordpressToken() {
      try {
        const name = self.rootStore.session.user?.name ?? 'placeholder'
        if (!name.includes('placeholder')) {
          const response = await self.rootStore.api.queryGetWordpressToken()
          logger.info('getWordpressToken response: ', response.getWordpressToken)
          self.rootStore.session.setWordpressToken(response.getWordpressToken.wordpressToken.jwt_token)
          self.rootStore.session.setWordpressTokenExpireTimestamp(response.getWordpressToken.wordpressToken.expires_in)
        }
      } catch (error) {
        logger.error('getWordpressToken error', error)
      }
    },
    async resetDailyInspiration() {
      try {
        const userId = self.rootStore.session.user?._id
        if (userId) {
          await self.rootStore.api.mutateResetDailyTask({ userId })
        }
      } catch (error) {
        logger.error('resetDailyInspiration', error)
      }
    },
    async resetDailyTask() {
      try {
        const userId = self.rootStore.session.user?._id
        if (userId) {
          await self.rootStore.api.mutateResetDailyInspiration({ userId })
        }
      } catch (error) {
        logger.error('resetDailyTask', error)
      }
    },
    async addDailyInspirationResponses(response: string) {
      try {
        const todaysDate = addDays(new Date(), self.taskDay)
        const formattedDate = format(todaysDate, defaultDateFormat)
        const id = self.dailyInspirationalQuoteData?.id ?? ''
        const userId = self.rootStore.session.user?._id
        if (userId && id) {
          const payload = {
            id,
            userId,
            response,
            date: formattedDate
          }
          const result = await self.rootStore.api.mutateAddDailyInspirationResponses(payload)
          logger.info('addDailyInspirationResponses ==>>', result)
          self.rootStore.session.setAllInspirationResponses([
            result.addDailyInspirationResponses,
            ...self.rootStore.session.allInspirationResponses
          ])
        }
      } catch (error) {
        logger.error('addDailyInspirationResponses error', error)
      }
    },
    async getDailyInspirationResponses() {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        const id = self.dailyInspirationalQuoteData?.id ?? ''
        const page = self.rootStore.session.pageNumber
        if (userId && id) {
          const response = await self.rootStore.api.queryGetDailyInspirationResponses(
            { id, userId, page },
            { fetchPolicy: 'no-cache' }
          )
          if (response.getDailyInspirationResponses.responses.length === 0) {
            self.rootStore.session.setHasMore(false)
          }
          if (page === 0) {
            self.rootStore.session.setAllInspirationResponses([
              ...response.getDailyInspirationResponses.userLatestResponse,
              ...response.getDailyInspirationResponses.responses
            ])
          } else {
            self.rootStore.session.setAllInspirationResponses([
              ...self.rootStore.session.allInspirationResponses,
              ...response.getDailyInspirationResponses.responses
            ])
          }
          logger.debug('getDailyInspirationResponses response', response.getDailyInspirationResponses)
        }
      } catch (error) {
        logger.error('getDailyInspirationResponses Error: ', error)
        throw error
      }
    },
    async deleteDailyInspirationResponse(responseId: string) {
      try {
        const userId = self.rootStore.session.user?._id
        if (userId) {
          const result = await self.rootStore.api.mutateDeleteDailyInspirationResponse({ userId, responseId })
          const allResponses = self.rootStore.session.allInspirationResponses
          const updatedResult = allResponses.filter(
            (responses) => responses._id !== result.deleteDailyInspirationResponse.responseId
          )
          self.rootStore.session.setAllInspirationResponses(updatedResult)
        }
      } catch (error) {
        logger.error('deleteDailyInspirationResponse error', error)
      }
    },
    async updateDailyInspirationResponses(responseMsg: string, responseId: string) {
      try {
        const userId = self.rootStore.session.user?._id
        if (userId) {
          const payload = {
            response: responseMsg,
            responseId,
            userId
          }
          const result = await self.rootStore.api.mutateUpdateDailyInspirationResponses(payload)
          const allResponsesSnapshot = getSnapshot(self.rootStore.session.allInspirationResponses) // Take a deep snapshot

          const updatedResult = allResponsesSnapshot.map((obj) =>
            obj._id === result.updateDailyInspirationResponses._id ? { ...obj, response: responseMsg } : obj
          )
          applySnapshot(self.rootStore.session.allInspirationResponses, updatedResult) // Apply deep snapshot
        }
      } catch (error) {
        logger.error('updateDailyInspirationResponses error', error)
      }
    },
    async updateInspirationResponsesEmojis(emoji: string, responseId: string) {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        if (userId) {
          const result = await self.rootStore.api.mutateUpdateInspirationResponsesEmojis({
            emoji,
            responseId,
            userId
          })
          const allResponsesSnapshot = getSnapshot(self.rootStore.session.allInspirationResponses) // Take a deep snapshot
          const updatedResult = allResponsesSnapshot.map((obj) =>
            obj._id === result.updateInspirationResponsesEmojis?.responseId
              ? { ...obj, emojis: [...result.updateInspirationResponsesEmojis.emojis] }
              : obj
          )
          applySnapshot(self.rootStore.session.allInspirationResponses, updatedResult) // Apply deep snapshot
        }
      } catch (error) {
        logger.error('updateInspirationResponsesEmojis Error: ', error)
        throw error
      }
    },
    async addInspirationResponsesReport(reason: string, responseId: string) {
      try {
        const userId = self.rootStore.session.user?._id
        if (userId) {
          const result = await self.rootStore.api.mutateAddInspirationResponsesReport({ reason, responseId, userId })
          logger.debug('addInspirationResponsesReport ==>>', result)
        }
      } catch (error) {
        logger.error('addInspirationResponsesReport error', error)
      }
    },
    async getUserResponseCount() {
      try {
        const todaysDate = addDays(new Date(), self.taskDay)
        const formattedDate = format(todaysDate, defaultDateFormat)
        const userId = self.rootStore.session.user?._id
        const inspirationId = self.dailyInspirationalQuoteData?.id
        if (userId && inspirationId) {
          const result = await self.rootStore.api.queryGetUserDailyInspirationResponsesCount(
            { date: formattedDate, inspirationId, userId },
            { fetchPolicy: 'no-cache' }
          )
          logger.debug('getUserResponseCount ==>>', result)
          self.rootStore.session.setUserResponseCount(result.getUserDailyInspirationResponsesCount)
        }
      } catch (error) {
        logger.error('getUserResponseCount error', error)
      }
    }
  }))
  .actions((self) => ({
    async resetDaily() {
      logger.info('resetDaily')
      self.clearTaskDay()
      self.clearSelectedDailyTask()
      self.clearUserResponseCount()
      self.setPageNumber(0)
      await Promise.allSettled([self.resetDailyInspiration(), self.resetDailyTask()])
      await Promise.allSettled([self.getDailyTask(), self.getDailyInspirationalQuote()])
    }
  }))
  .actions((self) => {
    const afterAttach = async () => {
      // if (self.rootStore.session.isLoggedIn) {
      //   await self.getWordpressToken()
      // }
    }
    return { afterAttach }
  })

export type SessionDailyStore = Instance<typeof SessionDailyStoreModel>
export type SessionDailyStoreSnapshot = SnapshotOut<typeof SessionDailyStoreModel>
